import { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { useAuth0 } from "@auth0/auth0-react";
import { CircularProgress } from "@mui/material";
import { Navigate, useLocation } from 'react-router-dom';

import Navigation from "Navigation";
import NavBarMui from "components/appbar/NavBarMui";
import DrawerMenu from "components/drawer/DrawerMenu";
import LandingDrawer from "components/landingDrawer/LandingDrawer";
import AddProjectSidebar from "components/sidebar/AddProjectSidebar";
import AddEcomSidebar from "components/sidebar/AddEcomSidebar";
import useAuth from 'hooks/useAuth';
import { unauthenticatedPaths } from "data/paths";

import SE2050Background from "./assets/landing-page-image.png";
import SE2050Logo from "./assets/SEI_White_Logo.png";
import CheckCircle from "./assets/icons/CheckCircle.png";
import WarningCircle from "./assets/icons/WarningCircle.png";

export const drawerWidth = 240;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
  usePadding?: boolean;
}>(({ theme, open, usePadding }) => ({
  flexGrow: 1,
  padding: !usePadding ? theme.spacing(3) : 0,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...((open || usePadding) && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

export const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "space-between",
  marginLeft: "10px",
}));

export default function Frame() {
  const { isAuthenticated, error, logout } = useAuth0();
  const [open, setOpen] = useState(true);
  const [isAddingProject, setIsAddingProject] = useState(false); // WIP
  const [isAddingEcom, setIsAddingEcom] = useState(false); // WIP
  const [currentStep, setCurrentStep] = useState<number | null>(null);

  const location = useLocation();

  const { isLoading } = useAuth();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (location.pathname === '/add-project/1' || location.pathname === '/add-project/2' || location.pathname === '/add-project/3'
    || location.pathname === '/add-project/4' || location.pathname === '/add-project/5' || location.pathname === '/add-project/6'
    || location.pathname === '/add-project/7' || location.pathname === '/add-project/8') {
      setIsAddingProject(true);
      setIsAddingEcom(false);
      const pathParts = location.pathname.split('/');
      const step = parseInt(pathParts[pathParts.length - 1], 10);
      if (!isNaN(step)) {
        setCurrentStep(step);
      } else {
        setCurrentStep(null);
      }
    } else if (location.pathname === '/add-ecom/1' || location.pathname === '/add-ecom/2' || location.pathname === '/add-ecom/3'
      || location.pathname === '/add-ecom/4' || location.pathname === '/add-ecom/5' || location.pathname === '/add-ecom/6') {
      setIsAddingEcom(true);
      setIsAddingProject(false);
      const pathParts = location.pathname.split('/');
      const step = parseInt(pathParts[pathParts.length - 1], 10);
      if (!isNaN(step)) {
        setCurrentStep(step);
      } else {
        setCurrentStep(null);
      }
    } else {
      setIsAddingProject(false);
      setIsAddingEcom(false);
      setCurrentStep(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    if ((error?.message ?? '') === 'Thank you for signing up.') {
      logout({ returnTo: window.location.origin });
    }
  }, [error?.message, logout]);

  if (isLoading) {
    return (
      <div
        style={{
          margin: "20%",
          textAlign: "center",
        }}
      >
        <CircularProgress size="100px" />
      </div>
    );
  }

  else if (error) {
    console.log(error.message)
    let iconSrc = WarningCircle;
    let errorMessage = 'Something went wrong.'
    let errorDescription = 'Something seems to have gone wrong. Please try signing in again and if this issue persists, contact an admin.'
    if (error.message){
      switch (error.message) {
        case 'Your account is not active yet.':
          errorMessage = error.message;
          errorDescription = 'Your account will become active following approval by an SE 2050 Administrator or your Firm Admin, and you will receive an email at the provided email address. Please allow up to two weeks.'
          iconSrc = CheckCircle
          break;
        case 'Thank you for signing up.':
          errorMessage = error.message;
          errorDescription = 'Your account will become active following approval by an SE 2050 Administrator or your Firm Admin, and you will receive an email at the provided email address. Please allow up to two weeks.'
          iconSrc = CheckCircle
          break;
      }
    }
    return (
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
          <LandingDrawer open={open} handleDrawerClose={handleDrawerClose} />
          <div style={{
            position: 'absolute',
            backgroundColor: '#012a52',
            height: '100%',
            width: '100%'
          }}>
              <div style={{
                overflow: 'auto', 
                paddingTop: '5%', 
                paddingLeft: 25, 
                paddingRight: 25, 
                width: '35%', 
                position: 'absolute', 
                bottom: '30%', 
                right: '10%', 
                background: '#fff', 
                height: '50%', 
                maxHeight: 500, 
                textAlign: 'center',
                borderRadius: 25,
                }}>
              <img src={iconSrc} alt="Circle with checkmark" style={{objectFit: 'cover', width: 120}}/>
              <h1>{errorMessage}</h1>
              <p>{errorDescription}</p>
              </div>
              <img src={SE2050Logo} alt="SE 2050 Logo" style={{objectFit: 'cover', width: '30%', position: 'absolute', bottom: 33, right: 33}}/>
          </div>
      </Box>
    );
  }

  // if an unauthenticated user is trying to access a protected page, redirect them to the landing page
  else if (!isLoading && !isAuthenticated && !unauthenticatedPaths.has(location.pathname)) {
    return <Navigate to={"/landing"} />;
  }

  else if (location.pathname === '/landing'){
    if (isAuthenticated) {
      return <Navigate to={"/dashboard"} replace />;
    }

    return (
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
          <LandingDrawer open={open} handleDrawerClose={handleDrawerClose} />
          <div style={{
            position: 'absolute',
            backgroundColor: '#012a52',
            height: '100%',
            width: '100%'
          }}>
              <img src={SE2050Logo} alt="SE 2050 Logo" style={{objectFit: 'cover', width: '30%', position: 'absolute', bottom: 33, right: 33}}/>
          </div>
      </Box>
    );
  }

  else {
    return (
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        {!isAddingProject && !isAddingEcom ? 
        <>
          <NavBarMui open={open} handleDrawerOpen={handleDrawerOpen} />
          <DrawerMenu open={open} handleDrawerClose={handleDrawerClose} />
        </> : isAddingProject ? <AddProjectSidebar stepNumber={currentStep}></AddProjectSidebar> : <AddEcomSidebar stepNumber={currentStep}></AddEcomSidebar>}
        <Main open={open} usePadding={isAddingProject}>
          {!isAddingProject ? <DrawerHeader /> : null}
          <Navigation />
        </Main>
      </Box>
    );
  }
}
